/**
 * Axios API Configuration
 * 
 * Provides a configured axios instance with:
 * - Automatic JWT token injection
 * - Organization context headers
 * - Request/Response interceptors
 * - Environment-based base URLs
 */

import axios, { AxiosInstance } from "axios";
import { localStoreConfig } from "./constants";
import { setupInterceptors } from "./axiosInterceptor";

// Configuration parameters interface
interface AxiosApiParams {
  token?: string;
  organizationId?: string;
  contentType?: string;
}

// Headers interface for proper typing
interface Headers {
  "Content-Type": string;
  Authorization?: string;
  "x-dharamkaata-organization-id"?: string;
  [key: string]: string | undefined;
}

// Environment-based API base URLs
type Environment = 'development' | 'staging' | 'production';

const API_BASE_URLS: Record<Environment, string> = {
  development: "http://localhost:3344/api/v1",
  staging: "https://fm2jklu89l.execute-api.ap-south-1.amazonaws.com/dev",
  production: process.env.REACT_APP_API_BASE_URL || "", // Set via environment variable
};

// Get current environment
const getEnvironment = (): Environment => {
  const env = process.env.NODE_ENV as Environment;
  return env || 'development';
};

// Get base URL for current environment
const getBaseURL = (): string => {
  const env = getEnvironment();
  return API_BASE_URLS[env] || API_BASE_URLS.development;
};

/**
 * Create configured axios instance
 * @param params - Configuration parameters (all optional)
 * @returns Configured axios instance
 */
export default function axiosApi(params: AxiosApiParams = {}): AxiosInstance {
  // Get authentication and context data
  const token = params.token || localStorage.getItem(localStoreConfig.secureToken);
  const organizationId = params.organizationId || localStorage.getItem(localStoreConfig.selectedOrganizationId);
  
  // Build headers
  const headers: Headers = {
    "Content-Type": params.contentType || "application/json",
  };

  // Add JWT token for authenticated requests - only if token exists
  if (token) {
    // Ensure we don't double-add Bearer prefix
    const cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
    headers.Authorization = `Bearer ${cleanToken}`;
  }
  // Don't set Authorization header at all if no token is available

  // Add organization context for protected routes
  // Note: This header is only required for protected business routes, not auth routes
  if (organizationId) {
    headers["x-dharamkaata-organization-id"] = organizationId;
  }


  // Always create a fresh instance with current headers
  const axiosInstance = axios.create({
    baseURL: getBaseURL(),
    timeout: 30000, // 30 second timeout
    headers,
  });
  
  // Setup request/response interceptors
  setupInterceptors(axiosInstance);

  return axiosInstance;
}
